import React from 'react';
import clsx from 'clsx';
import { useInPane } from '../surface/PaneBody';

export interface GlassCardProps {
  children: React.ReactNode;
  /** Optional header row: small muted icon + label, right-side actions */
  icon?: React.ReactNode;
  /** Already-translated header label (string or inline node, e.g. label + count) */
  title?: React.ReactNode;
  /** Optional muted line under the title */
  subtitle?: string;
  /** Right side of the header row (buttons, counts, links) */
  actions?: React.ReactNode;
  /**
   * Padding for the body. Default 'md' (p-4). 'none' for tables/lists that
   * manage their own. 'xl' (p-6) is for a card that IS the screen - a single
   * run's progress panel floating on an otherwise empty canvas, where card
   * padding is the page's margin.
   */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Header density. Default keeps the standard card header; 'compact' is the
   * tighter bar used by floating panels, where the header is chrome rather
   * than a section heading.
   */
  density?: 'default' | 'compact';
  /** Status tint for urgency/status cards (due dates, alerts). Default 'default' (plain glass). */
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  className?: string;
  /** Classes for the body wrapper - e.g. making it the flex child that scrolls. */
  bodyClassName?: string;
  /**
   * Attributes for the header ROW itself (not the title). Lets a caller make
   * the whole bar interactive - a floating panel uses it as its drag handle,
   * where making only the title text draggable leaves most of the bar dead.
   */
  headerProps?: React.HTMLAttributes<HTMLDivElement>;
  /**
   * OPT-IN, and off everywhere by default: when this card is the ROOT of a
   * surface pane's body, drop the glass shell and keep only the header row as
   * a thin toolbar.
   *
   * A pane already IS a glass card with a header, so a root-level card inside
   * one is a card in a card. Only the root of a pane body should set this —
   * cards used as sections INSIDE a pane (the matter context pane stacks
   * several) are meant to look like cards. Outside a pane it changes nothing,
   * which is why it is safe to add to a shared component.
   */
  paneAware?: boolean;
  /**
   * OPT-IN: the card fills the height it is given rather than being sized by
   * its content, and its body becomes the flex column the content fills.
   *
   * This is what lets a list section be as tall as the pane it sits in. A card
   * is a content-sized block by default, so a `h-full` child of one resolves
   * against `auto` and quietly grows to all of its rows instead of scrolling —
   * the height chain has to be unbroken from the pane down, and this is the
   * card's link in it. Only for bodies that own their own scroller; a field
   * form wants to be sized by its fields.
   */
  fill?: boolean;
  /** Inline style - used for per-item reveal delays (`--reveal-delay`). */
  style?: React.CSSProperties;
  'data-tour-id'?: string;
  'data-testid'?: string;
}

const PADDING: Record<NonNullable<GlassCardProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
  xl: 'p-6',
};

const HEADER_DENSITY: Record<NonNullable<GlassCardProps['density']>, string> = {
  default: 'px-5 py-3.5',
  compact: 'px-3 py-2',
};

const TONE: Record<NonNullable<GlassCardProps['tone']>, string> = {
  default: '',
  success: '!border-success-200 dark:!border-success-800/50 bg-success-50/40 dark:bg-success-900/10',
  warning: '!border-warning-200 dark:!border-warning-800/50 bg-warning-50/40 dark:bg-warning-900/10',
  danger: '!border-danger-200 dark:!border-danger-800/50 bg-danger-50/40 dark:bg-danger-900/10',
  info: '!border-info-200 dark:!border-info-800/50 bg-info-50/40 dark:bg-info-900/10',
};

/**
 * THE standard PONS container - a liquid-glass panel. Wraps the shared
 * `.glass` utility (single source of truth in src/index.css) with the
 * canonical rounding, shadow, and optional header row used across the
 * app. Use this for every new card/panel/section container; do not
 * hand-roll `bg-white dark:bg-gray-*` cards.
 *
 *   <GlassCard icon={<PiFileTextLight />} title={t('Drafts')} actions={<Button .../>}>
 *     ...
 *   </GlassCard>
 */
const GlassCard: React.FC<GlassCardProps> = ({
  children,
  icon,
  title,
  subtitle,
  actions,
  padding = 'md',
  density = 'default',
  tone = 'default',
  className,
  bodyClassName,
  headerProps,
  paneAware = false,
  fill = false,
  style,
  ...rest
}) => {
  const inPane = useInPane();
  const flush = paneAware && inPane;

  return (
  <div
    className={clsx(
      flush
        ? 'flex flex-col h-full min-h-0'
        : ['glass rounded-lg shadow-sm', TONE[tone], fill && 'flex h-full min-h-0 flex-col'],
      className
    )}
    style={style}
    {...rest}
  >
    {(title || actions) && (
      <div
        {...headerProps}
        className={clsx(
          'flex items-center justify-between gap-2 border-b border-hairline-soft',
          flush ? HEADER_DENSITY.compact : HEADER_DENSITY[density],
          (flush || fill) && 'flex-shrink-0',
          headerProps?.className
        )}
      >
        <div>
          <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
            {icon && <span className="[&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
            {title && <span className="text-xs font-medium">{title}</span>}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    {/* In a pane the body IS the scroll region: the panel's own scroll
        container is above the header, so a flush card has to own it. */}
    <div
      className={clsx(
        PADDING[padding],
        flush && 'flex-1 min-h-0 overflow-y-auto custom-scrollbar-main',
        // A filled card's body is a column the content can fill; unlike a
        // pane's, it does NOT scroll - the list inside owns the only scroller.
        !flush && fill && 'flex min-h-0 min-w-0 flex-1 flex-col',
        bodyClassName
      )}
    >
      {children}
    </div>
  </div>
  );
};

export default GlassCard;
